/**
 * Loading a lorry, and seeing where everything got to.
 *
 * LSK's outbound work is keyed on the vehicle: several sales orders picked together the day before
 * shipping, staged at the loading area, then whatever fits goes on the lorry and the rest comes
 * back. They already work this way by hand — 26 orders named `VFY 1018(04.09.2026)` and the like.
 *
 * Two things this adds. A PLAN, so the handheld refuses an item that is not on the order instead of
 * discovering it at the customer. And a board that answers the question the plan makes askable:
 * picked, loaded, and what came back.
 *
 * ⚠️ Nothing here posts to SQL Accounting. LSK were explicit — this verifies, it does not account.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { Card, Table, Loading, Empty, cell } from '../components/ui';
import { LorryOrders } from './LorryOrders';

type SoRow = {
  docno: string;
  docdate: string;
  partner_name: string | null;
  lines_: number;
  outstanding: string;
};

type Preview = {
  preview?: boolean;
  orderNo: string;
  totalQty?: string;
  lines?: Array<{ itemcode: string; outstandingQty: string; sourceDocNo: string }>;
  dropped?: Array<{ docno: string; itemcode: string }>;
  payload?: { planQty: number; data: unknown[] };
  unchoosable?: string[];
  dryRun?: boolean;
  simulated?: boolean;
};

type Leg = { orderNo: string; status: number; qty: number; plan: number; date: string | null };
type BoardRow = {
  plate: string;
  date: string | null;
  planned: number;
  picked: number;
  notLoaded: number;
  loaded: number;
  mismatched: boolean;
  legs: { picked: Leg[]; returned: Leg[]; shipped: Leg[] };
};

const today = () => new Date().toISOString().slice(0, 10);

export default function Lorry() {
  const qc = useQueryClient();
  const [plate, setPlate] = useState('');
  const [shipDate, setShipDate] = useState(today());
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<SoRow[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [sent, setSent] = useState<Preview | null>(null);
  const [tab, setTab] = useState<'load' | 'orders'>('load');
  /**
   * The lorry order being edited, or null when building a new one (LSK, 2026-10-31). Allowed only
   * while nobody has scanned it; the plate and date are its number, so they stay fixed.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const startEdit = async (orderNo: string) => {
    setEditError(null);
    try {
      const r = await api.get<{
        orderNo: string;
        plate: string;
        shipDate: string;
        orders: SoRow[];
      }>(`/api/v1/rfid/lorry/${encodeURIComponent(orderNo)}/edit`);
      setPlate(r.plate);
      setShipDate(r.shipDate);
      setChosen(r.orders);
      setSearch('');
      setPreview(null);
      setSent(null);
      send.reset();
      setEditing(r.orderNo);
      setTab('load');
    } catch (err) {
      setEditError((err as Error).message);
    }
  };

  // Listed straight away, not only once something is typed: the search box was the only way
  // forward and nothing pointed at it — "i key in lorry number, then how to proceed?" (2026-10-04).
  const found = useQuery({
    queryKey: ['lorry', 'search', search.trim(), editing],
    queryFn: () =>
      api.get<{ orders: SoRow[] }>(
        `/api/v1/rfid/lorry/search?code=${encodeURIComponent(search.trim())}` +
          // While editing, this lorry's own sales orders stay listed, so a removed one can return.
          (editing ? `&except=${encodeURIComponent(editing)}` : ''),
      ),
  });

  const board = useQuery({
    queryKey: ['lorry', 'board'],
    queryFn: () => api.get<{ board: BoardRow[]; unmatched: unknown[] }>('/api/v1/rfid/lorry/board'),
  });

  const send = useMutation({
    mutationFn: (confirm: boolean) =>
      api.post<Preview>('/api/v1/rfid/lorry', {
        plate,
        shipDate,
        docnos: chosen.map((c) => c.docno),
        confirm,
        ...(editing ? { replaces: editing } : {}),
      }),
    onSuccess: (r, confirm) => {
      if (confirm) {
        setSent(r);
        setEditing(null);
        // The sent order joins the history, and its sales orders leave the list to add from.
        void qc.invalidateQueries({ queryKey: ['lorry'] });
      } else setPreview(r);
    },
  });

  const add = (o: SoRow) => {
    setPreview(null);
    setSent(null);
    setChosen((c) => (c.some((x) => x.docno === o.docno) ? c : [...c, o]));
  };

  return (
    <div className="space-y-6">
      {/* Two tabs (LSK, 2026-10-04): building a load, and the orders already sent. */}
      <div role="tablist" className="flex gap-2 border-b border-slate-200">
        {(
          [
            ['load', 'Load a lorry'],
            ['orders', 'Lorry orders'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === key
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'load' && (
        <Card
          title="Load a lorry"
          subtitle="Add the sales order — or several travelling together — then send one planned order to the handheld. One order on its own is fine. When two orders want the same item, the first one listed is filled first."
        >
          {editing && (
            <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              Editing <strong className="font-mono">{editing}</strong>. Add or remove sales orders,
              then preview and send — it replaces the order already on the handheld. Only possible
              while nobody has started scanning it. The plate and date cannot change.
            </p>
          )}
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-600">
              <span className="mb-1 block">1 · Lorry plate</span>
              <input
                value={plate}
                onChange={(e) => setPlate(e.target.value)}
                placeholder="VFY 1018"
                disabled={editing !== null}
                className="w-40 rounded border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="text-xs text-slate-600">
              <span className="mb-1 block">Shipping date</span>
              <input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
                disabled={editing !== null}
                className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>
            {/* Start the next lorry from nothing (LSK, 2026-10-04). Clears the screen only — an
              order already sent stays sent; it is listed under "Lorry orders sent". */}
            <button
              type="button"
              onClick={() => {
                setPlate('');
                setShipDate(today());
                setSearch('');
                setChosen([]);
                setPreview(null);
                setSent(null);
                setEditing(null);
                send.reset();
              }}
              className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50"
            >
              {editing ? 'Stop editing' : 'Reset'}
            </button>
          </div>

          <h3 className="mb-1 text-sm font-semibold text-slate-700">
            2 · Add the sales orders going on this lorry
          </h3>
          <p className="mb-2 text-xs text-slate-500">
            Press <strong>Add</strong> on each one, in the order they should be filled. Type to
            narrow the list by order number or customer.
          </p>
          <form
            className="flex flex-wrap items-center gap-3"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Narrow by order number or customer"
              aria-label="Find a sales order"
              className="w-80 rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </form>

          {chosen.length > 0 && (
            <div className="mt-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">
                3 · On this lorry ({chosen.length}) — filled in this order. Preview, then send.
              </h3>
              <Table
                head={['#', 'Sales order', 'Customer', 'Outstanding', '']}
                rows={chosen.map((o, i) => [
                  i + 1,
                  cell(o.docno, <span className="font-mono text-xs">{o.docno}</span>),
                  o.partner_name ?? '—',
                  o.outstanding,
                  cell(
                    '',
                    <button
                      type="button"
                      onClick={() => {
                        setChosen((c) => c.filter((x) => x.docno !== o.docno));
                        setPreview(null);
                      }}
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                    >
                      Remove
                    </button>,
                  ),
                ])}
              />

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={plate.trim() === '' || send.isPending}
                  onClick={() => send.mutate(false)}
                  className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {send.isPending && send.variables === false ? 'Checking…' : 'Preview the order'}
                </button>
                <button
                  type="button"
                  disabled={!preview || send.isPending}
                  onClick={() => send.mutate(true)}
                  className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                >
                  {send.isPending && send.variables === true
                    ? 'Sending…'
                    : editing
                      ? 'Send changes to handheld'
                      : 'Send to handheld'}
                </button>
                {plate.trim() === '' ? (
                  <span className="text-xs text-amber-700">
                    ← enter the lorry plate (step 1) first
                  </span>
                ) : (
                  !preview && (
                    <span className="text-xs text-slate-500">
                      ← preview first; the order reaches a handheld immediately
                    </span>
                  )
                )}
              </div>

              {send.isError && (
                <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                  {(send.error as Error).message}
                </p>
              )}

              {preview && !sent && (
                <div className="rounded border border-slate-200 bg-brand-50 px-4 py-3 text-sm text-slate-700">
                  <p>
                    <span className="font-mono font-semibold">{preview.orderNo}</span> —{' '}
                    <strong>{preview.payload?.data.length ?? 0}</strong> plan line(s),{' '}
                    <strong>{preview.payload?.planQty ?? 0}</strong> unit(s) to pick.
                    {preview.dryRun && (
                      <span className="text-slate-500"> DRY_RUN is on — nothing will be sent.</span>
                    )}
                  </p>
                  {(preview.dropped?.length ?? 0) > 0 && (
                    <p className="mt-1 text-xs text-slate-600">
                      Left off: {preview.dropped?.map((d) => `${d.docno}/${d.itemcode}`).join(', ')}{' '}
                      — charge lines with nothing physical to scan.
                    </p>
                  )}
                  {(preview.unchoosable?.length ?? 0) > 0 && (
                    <p className="mt-1 text-xs text-amber-800">
                      No scannable code for: {preview.unchoosable?.join(', ')} — check the colour
                      mappings, these lines would be unscannable.
                    </p>
                  )}
                </div>
              )}

              {sent && (
                <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  {sent.simulated ? (
                    <>Simulated — DRY_RUN is on, nothing was sent.</>
                  ) : (
                    <>
                      Sent as <strong className="font-mono">{sent.orderNo}</strong>. It should
                      appear on the warehouse handheld now.
                    </>
                  )}
                </p>
              )}
            </div>
          )}

          {found.isLoading && <Loading />}
          {/* Never a blank box. A failed search and an empty one looked identical, and with nothing
            listed nothing can be added — LSK read it as search, add AND preview all broken. */}
          {found.isError && (
            <p className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
              Could not search sales orders: {(found.error as Error).message}
            </p>
          )}
          {found.data && found.data.orders.length === 0 && (
            <p className="mt-3 text-sm text-slate-500">
              {search.trim() === ''
                ? 'No sales order has anything outstanding to deliver.'
                : `No sales order with anything outstanding matches “${search.trim()}”.`}{' '}
              Only orders with something still to deliver are listed — one delivered in full, or
              raised in the last half hour (the sales orders sync every 30 minutes), will not
              appear.
            </p>
          )}
          {found.data && found.data.orders.length > 0 && (
            <div className="mt-3">
              <Table
                head={['Sales order', 'Date', 'Customer', 'Lines', 'Outstanding', '']}
                rows={found.data.orders.map((o) => [
                  cell(o.docno, <span className="font-mono text-xs">{o.docno}</span>),
                  o.docdate?.slice(0, 10) ?? '—',
                  o.partner_name ?? '—',
                  o.lines_,
                  o.outstanding,
                  cell(
                    '',
                    <button
                      type="button"
                      onClick={() => add(o)}
                      disabled={chosen.some((c) => c.docno === o.docno)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50 disabled:text-slate-400"
                    >
                      {chosen.some((c) => c.docno === o.docno) ? 'added' : 'Add'}
                    </button>,
                  ),
                ])}
              />
            </div>
          )}

          {found.data && found.data.orders.length >= 25 && (
            <p className="mt-2 text-xs text-slate-500">
              Showing the 25 most recent — type an order number or customer to find others.
            </p>
          )}
        </Card>
      )}
      {tab === 'orders' && (
        <>
          <Card
            title="Lorry orders sent"
            subtitle="Every lorry order sent from this screen, as Smartlink holds it now. Once the pick is submitted, create the return order for whatever does not go on the lorry."
          >
            {editError && (
              <p className="mb-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                {editError}
              </p>
            )}
            <LorryOrders onEdit={(orderNo) => void startEdit(orderNo)} />
          </Card>

          <Card
            title="Where everything got to"
            subtitle="Per lorry: what was planned, what the warehouse picked, what came back from the loading area, and what actually left."
          >
            {board.isLoading ? (
              <Loading />
            ) : (board.data?.board ?? []).length === 0 ? (
              <Empty>No lorry activity in the last two weeks.</Empty>
            ) : (
              <Table
                head={['Lorry', 'Date', 'Planned', 'Picked', 'Not loaded', 'Loaded', '']}
                rows={(board.data?.board ?? []).map((r) => [
                  cell(r.plate, <span className="font-mono text-xs font-semibold">{r.plate}</span>),
                  r.date ?? '—',
                  // A manual order has no plan — saying "0" would read as "nothing was due".
                  r.planned === 0
                    ? cell(0, <span className="text-slate-400">no plan</span>)
                    : r.planned,
                  r.picked,
                  r.notLoaded === 0
                    ? cell(0, <span className="text-slate-400">—</span>)
                    : cell(
                        r.notLoaded,
                        <span className="font-medium text-amber-700">{r.notLoaded}</span>,
                      ),
                  cell(r.loaded, <span className="font-semibold">{r.loaded}</span>),
                  cell(
                    '',
                    r.mismatched ? (
                      /* More came back than went out: the two scans are not the same lorry's work. */
                      <span className="text-xs text-alert">more returned than picked — check</span>
                    ) : (
                      <span className="text-xs text-slate-400">
                        {r.legs.picked.length + r.legs.returned.length + r.legs.shipped.length}{' '}
                        order(s)
                      </span>
                    ),
                  ),
                ])}
              />
            )}
            {(board.data?.unmatched?.length ?? 0) > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                {board.data?.unmatched.length} order(s) had no readable lorry plate and are not
                grouped above — they are named for a place or a customer rather than a vehicle.
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
